import { roundTo } from "round-to";
import { months, sumQuantity, sumTotalValue } from "../../../utils/helper";

// Module-scope helper
const filterByTotal = (data) => data?.filter(item => item.somaNoTotal) || [];

function groupByProductGroup(data) {

    const groupedData = {};

    data?.forEach((item) => {
        const group = item.grupoProdutoDescricao;
        if (!groupedData[group]) {
            groupedData[group] = {
                group: group,
                records: [],
            };
        }
        groupedData[group].records.push(item);
    });

    return Object.values(groupedData);
}

function groupByProduct(data) {

    const groupedData = {};

    data?.forEach((item) => {
        const product = item.descricao;
        if (!groupedData[product]) {
            groupedData[product] = {
                product: product,
                records: [],
            };
        }
        groupedData[product].records.push(item);
    });

    return Object.values(groupedData);
}


export const CumulativeSalesByProductGroup = (sales) => {
    const existsCurrentMonthSales = sales && sales.currentMonthSalesProduct && sales.currentMonthSalesProduct.length
    let result = {
        caption: 'Top 10 Grupo de produto',
        data: [],
        index: "productGroup",
        categories: [],
        autoMinValue: true,
        colors: ["indigo", "cyan"],

    }

    if (!existsCurrentMonthSales) {
        return result
    }

    const actualMonth = sales.currentMonth - 1;
    let lastMonth = actualMonth - 1

    if (lastMonth < 0) {
        lastMonth = 11
    }

    const currentMonthDescription = `${months[actualMonth]}`;
    const lastMonthDescription = `${months[lastMonth]}`;

    result.categories.push(lastMonthDescription, currentMonthDescription)

    const groupedDataCurrentMonth = groupByProductGroup(sales.currentMonthSalesProduct)
    const groupedDataLastMonth = groupByProductGroup(sales.lastMonthSalesProduct)


    function filterByProductGroup(data, productGroup) {
        return data?.find(item => item?.group?.toUpperCase() === productGroup?.toUpperCase());
    }

    function extractProductGroup(data) {
        const groups = data.map(item => item.group?.toUpperCase());

        return groups.filter((value, index) => groups.indexOf(value?.toUpperCase()) === index);
    }

    const groupedData = [...groupedDataCurrentMonth, ...groupedDataLastMonth];
    const productGroupList = extractProductGroup(groupedData)

    let chartData = [];

    productGroupList.forEach(productGroup => {
        chartData.push({
            productGroup: productGroup,
            [lastMonthDescription]: sumTotalValue(filterByProductGroup(groupedDataLastMonth, productGroup)?.records),
            [currentMonthDescription]: sumTotalValue(filterByProductGroup(groupedDataCurrentMonth, productGroup)?.records),
        });
    })

    chartData.sort((a, b) => b[currentMonthDescription] - a[currentMonthDescription]);
    const extractedList = chartData.slice(0, 10);

    result.data = extractedList

    return result
}

export const CumulativeSalesByProduct = (sales) => {
    const existsCurrentMonthSales = sales && sales.currentMonthSalesProduct && sales.currentMonthSalesProduct.length
    let result = {
        height: 'h-[800px]',
        caption: 'Top produtos',
        data: [],
        yAxisWidth: 180,
        index: "description",
        categories: [],
        layout: 'vertical',
    }

    if (!existsCurrentMonthSales) {
        return result
    }

    const actualMonth = sales.currentMonth - 1;
    let lastMonth = actualMonth - 1

    if (lastMonth < 0) {
        lastMonth = 11
    }

    const currentMonthDescription = `${months[actualMonth]}`;
    const lastMonthDescription = `${months[lastMonth]}`;

    result.categories.push(lastMonthDescription, currentMonthDescription)

    const groupedDataCurrentMonth = groupByProduct(sales.currentMonthSalesProduct)
    const groupedDataLastMonth = groupByProduct(sales.lastMonthSalesProduct)


    function filterByProduct(data, product) {
        return data?.find(item => item?.product?.toUpperCase() === product?.toUpperCase());
    }

    function extractProduct(data) {
        const products = data.map(item => item.product?.toUpperCase());

        return [...new Set(products.flat())];
    }

    function extractStore(data) {
        const stores = data.map(item => item.records.map(rec => rec.unidadeName?.toUpperCase()));

        return [...new Set(stores.flat())];
    }

    const groupedData = [...groupedDataCurrentMonth, ...groupedDataLastMonth];

    const productList = extractProduct(groupedData)
    const storeList = extractStore(groupedData)
    
    let chartData = [];

    const totalCurrentMonth = sumTotalValue(sales.currentMonthSalesProduct)
    const totalLastMonth = sumTotalValue(sales.lastMonthSalesProduct)

    function calculateTotals(groupedData, product) {
        const totalsByUnidade = {};
    
        groupedData
            .filter(item => item.product === product)
            .forEach(item => {
                item.records.forEach(record => {
                    const unidadeName = record.unidadeName?.toUpperCase();
    
                    if (storeList.includes(unidadeName)) {
                        totalsByUnidade[unidadeName] = (totalsByUnidade[unidadeName] || 0) + parseFloat(record.valorTotal);
                    }
                });
            });
    
        return Object.entries(totalsByUnidade)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    }    

    productList.forEach(product => {


        let metricLastMonth = sumTotalValue(filterByProduct(groupedDataLastMonth, product)?.records)
        let quantityLastMonth = sumQuantity(filterByProduct(groupedDataLastMonth, product)?.records)
        let metricCurrentMonth = sumTotalValue(filterByProduct(groupedDataCurrentMonth, product)?.records)
        let quantityCurrentMonth = sumQuantity(filterByProduct(groupedDataCurrentMonth, product)?.records)

        let targetLastMonth = roundTo((metricLastMonth / totalLastMonth) * 100, 2)
        let targetCurrentMonth = roundTo((metricCurrentMonth / totalCurrentMonth) * 100, 2)

        const resultListLast = calculateTotals(groupedDataLastMonth, product);
        const resultListCurrent = calculateTotals(groupedDataCurrentMonth, product);

        chartData.push({
            description: product,
            [lastMonthDescription]: { metric: metricLastMonth, delta: targetLastMonth, quantity: quantityLastMonth, cumulativeList: resultListLast },
            [currentMonthDescription]: { metric: metricCurrentMonth, delta: targetCurrentMonth, quantity: quantityCurrentMonth, cumulativeList: resultListCurrent }
        });
    })



    result.data = chartData

    return result
}

export const kpiCards = (sales) => {
    if (!sales?.currentMonthSalesProduct?.length) {
        return {
            totalFaturado:     { metric: 0, delta: 0 },
            quantidadeVendida: { metric: 0, delta: 0 },
            ticketMedio:       { metric: 0, delta: 0 },
            produtoCampeao:    null,
        };
    }

    const currentData = filterByTotal(sales.currentMonthSalesProduct);
    const lastData = filterByTotal(sales.lastMonthSalesProduct);

    const totalAtual = currentData.reduce((s, i) => s + (i.dataCancelamento ? 0 : (parseFloat(i.valorTotal) || 0)), 0);
    const totalAnterior = lastData.reduce((s, i) => s + (i.dataCancelamento ? 0 : (parseFloat(i.valorTotal) || 0)), 0);

    const qtdAtual = currentData.reduce((s, i) => s + (i.dataCancelamento ? 0 : (parseFloat(i.quantidade) || 0)), 0);
    const qtdAnterior = lastData.reduce((s, i) => s + (i.dataCancelamento ? 0 : (parseFloat(i.quantidade) || 0)), 0);

    const ticketAtual = qtdAtual > 0 ? totalAtual / qtdAtual : 0;
    const ticketAnterior = qtdAnterior > 0 ? totalAnterior / qtdAnterior : 0;

    function delta(atual, anterior) {
        if (!anterior) return 0;
        return roundTo(((atual - anterior) / anterior) * 100, 1);
    }

    const productTotals = {};
    currentData.forEach(item => {
        if (item.dataCancelamento) return;
        const key = item.descricao;
        if (!productTotals[key]) {
            productTotals[key] = { descricao: item.descricao, grupo: item.grupoProdutoDescricao, total: 0 };
        }
        productTotals[key].total += parseFloat(item.valorTotal) || 0;
    });
    const campeao = Object.values(productTotals).sort((a, b) => b.total - a.total)[0] || null;

    return {
        totalFaturado: { metric: totalAtual, delta: delta(totalAtual, totalAnterior) },
        quantidadeVendida: { metric: qtdAtual, delta: delta(qtdAtual, qtdAnterior) },
        ticketMedio: { metric: ticketAtual, delta: delta(ticketAtual, ticketAnterior) },
        produtoCampeao: campeao,
    };
};

export const salesByWeekDay = (sales) => {
    if (!sales?.currentMonthSalesProduct || sales.currentMonth == null) {
        return { caption: 'Vendas por dia da semana', data: [], index: 'diaSemana', categories: [], layout: 'vertical', yAxisWidth: 130, colors: ["indigo", "cyan"], autoMinValue: true };
    }

    const currentData = filterByTotal(sales.currentMonthSalesProduct);
    const lastData = filterByTotal(sales.lastMonthSalesProduct);

    const weekLabels = ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"];

    const actualMonth = sales.currentMonth - 1;
    const lastMonthIdx = actualMonth - 1 < 0 ? 11 : actualMonth - 1;
    const currentMonthDescription = months[actualMonth];
    const lastMonthDescription = months[lastMonthIdx];

    const sumByWeekDay = (data) => {
        const acc = {};
        data.forEach(item => {
            if (item.dataCancelamento) return;
            const day = item.diaSemana;
            acc[day] = (acc[day] || 0) + (parseFloat(item.valorTotal) || 0);
        });
        return acc;
    };

    const currentByDay = sumByWeekDay(currentData);
    const lastByDay = sumByWeekDay(lastData);

    const chartData = weekLabels.map((label, idx) => ({
        diaSemana: label,
        [lastMonthDescription]: roundTo(lastByDay[idx] || 0, 2),
        [currentMonthDescription]: roundTo(currentByDay[idx] || 0, 2),
    }));

    return {
        caption: 'Vendas por dia da semana',
        data: chartData,
        index: 'diaSemana',
        categories: [lastMonthDescription, currentMonthDescription],
        layout: 'vertical',
        yAxisWidth: 130,
        colors: ["indigo", "cyan"],
        autoMinValue: true,
    };
};

export const salesByDay = (sales) => {
    if (!sales?.currentMonthSalesProduct || sales.currentMonth == null) {
        return { caption: 'Evolução diária no mês', data: [], index: 'dia', categories: [], colors: ["indigo", "cyan"] };
    }

    const currentData = filterByTotal(sales.currentMonthSalesProduct);
    const lastData = filterByTotal(sales.lastMonthSalesProduct);

    const actualMonth = sales.currentMonth - 1;
    const lastMonthIdx = actualMonth - 1 < 0 ? 11 : actualMonth - 1;
    const currentMonthDescription = months[actualMonth];
    const lastMonthDescription = months[lastMonthIdx];

    const sumByDay = (data) => {
        const acc = {};
        data.forEach(item => {
            if (item.dataCancelamento) return;
            const d = item.dia;
            acc[d] = (acc[d] || 0) + (parseFloat(item.valorTotal) || 0);
        });
        return acc;
    };

    const currentByDay = sumByDay(currentData);
    const lastByDay = sumByDay(lastData);

    const allDays = [...new Set([
        ...Object.keys(currentByDay),
        ...Object.keys(lastByDay)
    ])].map(Number).sort((a, b) => a - b);

    const chartData = allDays.map(d => ({
        dia: String(d),
        [lastMonthDescription]: roundTo(lastByDay[d] || 0, 2),
        [currentMonthDescription]: roundTo(currentByDay[d] || 0, 2),
    }));

    return {
        caption: 'Evolução diária no mês',
        data: chartData,
        index: 'dia',
        categories: [lastMonthDescription, currentMonthDescription],
        colors: ["indigo", "cyan"],
    };
};
